import React, { useState, useEffect } from 'react';
import DashboardLayout from './DashboardLayout';
import OverviewPage from './pages/OverviewPage';
import ProductsPage from './pages/ProductsPage';
import ApiKeyPage from './pages/ApiKeyPage';
import DomainsPage from './pages/DomainsPage';
import WidgetPage from './pages/WidgetPage';
import ImplementationPage from './pages/ImplementationPage';
import AnalyticsPage from './pages/AnalyticsPage';
import AdminUsersPage from './pages/AdminUsersPage';
import BlogAdminPage from './pages/BlogAdminPage';
import SubscriptionPage from './pages/SubscriptionPage';
import AccountPage from './pages/AccountPage';
import useAnalytics from './hooks/useAnalytics';
import { auth, user, admin, subscription } from './api';
import AuthPage from '../components/AuthPage';

const DASHBOARD_PAGES = ['overview', 'products', 'analytics', 'subscription', 'api-key', 'domains', 'widget', 'implementation', 'account', 'blog', 'admin'];

function pathForPage(page) {
  if (!page || page === 'overview') return '/dashboard';
  return `/dashboard/${page}`;
}

function pageFromPath(path) {
  if (!path || path === '/dashboard' || path === '/dashboard/') return 'overview';
  const segment = path.replace(/^\/dashboard\/?/, '').split('/')[0] || '';
  return DASHBOARD_PAGES.includes(segment) ? segment : 'overview';
}

const Dashboard = ({ onBack }) => {
  const [currentPage, setCurrentPage] = useState(() =>
    typeof window !== 'undefined' ? pageFromPath(window.location.pathname) : 'overview'
  );
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [notification, setNotification] = useState(null);
  const { trackPageView } = useAnalytics();

  const handleNavigate = (page) => {
    setCurrentPage(page);
    window.history.pushState({}, '', pathForPage(page));
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const session = await auth.getSession();
        if (!session?.user) {
          // Redirect to login or show login prompt
          setError('Not authenticated');
          return;
        }
        
        const profile = await user.getProfile();
        setUserData(profile.user);
        
        // Check admin status
        try {
          const { isSuperAdmin: isAdmin } = await admin.checkAccess();
          setIsSuperAdmin(isAdmin);
        } catch {
          setIsSuperAdmin(false);
        }

        // Check for subscription status from URL
        const urlParams = new URLSearchParams(window.location.search);
        const subscriptionStatus = urlParams.get('subscription');
        
        if (subscriptionStatus === 'success') {
          setNotification({
            type: 'success',
            message: 'Subscription activated successfully! Welcome to VisionLive.',
          });
          // Clean up URL and navigate to subscription page
          window.history.replaceState({}, '', '/dashboard');
          handleNavigate('subscription');
        } else if (subscriptionStatus === 'error' || subscriptionStatus === 'cancelled') {
          setNotification({
            type: 'error',
            message: 'Subscription was not completed. You can try again from the Subscription page.',
          });
          window.history.replaceState({}, '', '/dashboard');
          handleNavigate('subscription');
        }

        // Check for pending setup (one-time, e.g. catalog 25 products) from OAuth
        const pendingSetup = sessionStorage.getItem('pendingSetup');
        if (pendingSetup === '25') {
          sessionStorage.removeItem('pendingSetup');
          try {
            const { checkoutUrl } = await subscription.checkoutSetup('setup_25');
            if (checkoutUrl) {
              window.location.href = checkoutUrl;
              return;
            }
          } catch (err) {
            console.error('Failed to create setup checkout:', err);
            setNotification({
              type: 'error',
              message: 'Failed to start checkout. You can try again from the Subscription page.',
            });
            handleNavigate('subscription');
          }
        }

        // Check for pending plan from Google OAuth
        const pendingPlan = sessionStorage.getItem('pendingPlan');
        const pendingBilling = sessionStorage.getItem('pendingBilling');
        
        if (pendingPlan && pendingBilling) {
          // Clear the pending plan
          sessionStorage.removeItem('pendingPlan');
          sessionStorage.removeItem('pendingBilling');
          
          // Redirect to checkout
          try {
            const { checkoutUrl } = await subscription.checkout(pendingPlan, pendingBilling);
            if (checkoutUrl) {
              window.location.href = checkoutUrl;
              return;
            }
          } catch (err) {
            console.error('Failed to create checkout:', err);
            setNotification({
              type: 'error',
              message: 'Failed to start checkout. Please try again from the Subscription page.',
            });
            handleNavigate('subscription');
          }
        }
      } catch (err) {
        console.error('Auth check failed:', err);
        setError('Authentication failed');
      } finally {
        setLoading(false);
      }
    };
    
    checkAuth();
  }, []);

  // Sync state with browser back/forward
  useEffect(() => {
    const onPopState = () => setCurrentPage(pageFromPath(window.location.pathname));
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);
  
  // Track page views
  useEffect(() => {
    if (currentPage && !loading && userData) {
      trackPageView(currentPage);
    }
  }, [currentPage, loading, userData, trackPageView]);

  const renderPage = () => {
    switch (currentPage) {
      case 'overview':
        return <OverviewPage onNavigate={handleNavigate} />;
      case 'products':
        return <ProductsPage />;
      case 'analytics':
        return <AnalyticsPage />;
      case 'subscription':
        return <SubscriptionPage />;
      case 'api-key':
        return <ApiKeyPage />;
      case 'domains':
        return <DomainsPage />;
      case 'widget':
        return <WidgetPage />;
      case 'implementation':
        return <ImplementationPage />;
      case 'account':
        return <AccountPage />;
      case 'blog':
        return isSuperAdmin ? <BlogAdminPage /> : <OverviewPage onNavigate={handleNavigate} />;
      case 'admin':
        return isSuperAdmin ? <AdminUsersPage /> : <OverviewPage onNavigate={handleNavigate} />;
      default:
        return <OverviewPage onNavigate={handleNavigate} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/60">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <AuthPage 
        onBack={onBack}
        initialMode="login"
        onSuccess={() => {
          // Re-check auth after successful login
          setError(null);
          setLoading(true);
          window.location.reload();
        }}
      />
    );
  }

  return (
    <DashboardLayout
      currentPage={currentPage}
      onNavigate={handleNavigate}
      user={userData}
    >
      {/* Notification Banner */}
      {notification && (
        <div className={`mb-6 p-4 rounded-xl flex items-center justify-between ${
          notification.type === 'success' 
            ? 'bg-green-500/10 border border-green-500/20' 
            : 'bg-red-500/10 border border-red-500/20'
        }`}>
          <p className={notification.type === 'success' ? 'text-green-400' : 'text-red-400'}>
            {notification.message}
          </p>
          <button
            onClick={() => setNotification(null)}
            className={`ml-4 px-3 py-1 rounded-lg text-sm ${
              notification.type === 'success'
                ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
            }`}
          >
            Dismiss
          </button>
        </div>
      )}
      {renderPage()}
    </DashboardLayout>
  );
};

export default Dashboard;

